import {motion} from 'framer-motion';
import {
  Briefcase,
  ChartNoAxesCombined,
  Gift,
  HandCoins,
  Scale,
  Settings,
  Tag,
  TrendingUp,
} from 'lucide-react';
import * as React from 'react';
import {Link, useLocation} from 'react-router-dom';
import FocusTrap from 'src/FocusTrap';
import Layer from 'src/Layer/Layer';
import {Overlay} from 'src/Layer/Overlay';
import {cn} from 'src/lib/utils';
import {SETTINGS_PAGES} from 'src/SettingsView';
import type {LucideIcon} from 'lucide-react';

type Place = {
  path: string;
  label: string;
  icon: LucideIcon;
  // Other paths that count as being here (the Settings hub's own pages).
  within?: string[];
};

// Everything the tab bar doesn't hold, in the sidebar's order: the
// sidebar's folded "More" pages get a card of their own rather than a
// disclosure, since a sheet opened on purpose has the room.
const GROUPS: Place[][] = [
  [
    {path: '/analytics', label: 'Analytics', icon: ChartNoAxesCombined},
    {path: '/investments', label: 'Investments', icon: TrendingUp},
  ],
  [
    {path: '/net-worth', label: 'Net worth', icon: Scale},
    {path: '/payroll', label: 'Payroll', icon: HandCoins},
    {path: '/equity', label: 'Equity', icon: Briefcase},
    {path: '/benefits', label: 'Benefits', icon: Gift},
  ],
  [
    {path: '/categories', label: 'Categories', icon: Tag},
    {
      path: '/settings',
      label: 'Settings',
      icon: Settings,
      within: SETTINGS_PAGES.map((page) => page.path),
    },
  ],
];

const isAt = (pathname: string, place: Place) =>
  [place.path, ...(place.within ?? [])].some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );

/**
 * The phone's More: every place the tab bar doesn't hold, in a sheet that
 * rises over the tab bar from where the thumb already is. (Desktop keeps the
 * sidebar.) Picking a place, tapping outside, or Escape closes it.
 */
export const MoreSheet = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const {pathname} = useLocation();
  const sheetRef = React.useRef<HTMLDivElement | null>(null);

  // A modal sheet takes focus while it's open — the current place, else the
  // first — and hands it back to whatever opened it (More) when it closes.
  React.useEffect(() => {
    if (!open) return;
    const opener =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const frame = requestAnimationFrame(() => {
      const sheet = sheetRef.current;
      const target =
        sheet?.querySelector<HTMLElement>('[aria-current="page"]') ??
        sheet?.querySelector<HTMLElement>('a');
      // Without moving the page behind; then within the sheet, which scrolls
      // on a short screen, so the focused row is never left off-screen.
      target?.focus({preventScroll: true});
      target?.scrollIntoView({block: 'nearest'});
    });
    return () => {
      cancelAnimationFrame(frame);
      opener?.focus({preventScroll: true});
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  return (
    <Layer>
      {open && (
        <Overlay key="more-sheet" onOverlayClick={onClose} dimBackground>
          <motion.div
            ref={sheetRef}
            role="dialog"
            aria-modal
            aria-label="More"
            initial={{y: '100%'}}
            animate={{y: 0}}
            exit={{y: '100%'}}
            transition={{duration: 0.28, ease: [0.2, 0.8, 0.2, 1]}}
            className={cn(
              'bg-muted fixed inset-x-0 bottom-0 flex max-h-[92dvh] flex-col overflow-y-auto rounded-t-2xl',
              'px-3.5 pb-[max(env(safe-area-inset-bottom),1rem)] shadow-[0_-8px_30px_rgba(0,0,0,0.18)]',
            )}
          >
            <FocusTrap behavior="trap">
            <div className="flex shrink-0 justify-center pt-2 pb-1.5">
              <span className="bg-muted-foreground/30 h-1.5 w-9 rounded-full" />
            </div>
            <nav aria-label="More places" className="flex flex-col gap-3.5 pt-1.5">
              {GROUPS.map((group) => (
                <div
                  key={group[0].path}
                  className="bg-background ring-border flex flex-col overflow-hidden rounded-xl ring-1"
                >
                  {group.map((place) => {
                    const active = isAt(pathname, place);
                    const Icon = place.icon;
                    return (
                      <Link
                        key={place.path}
                        to={place.path}
                        onClick={onClose}
                        aria-current={active ? 'page' : undefined}
                        className={cn(
                          'text-foreground relative flex min-h-[50px] items-center gap-3 px-3.5 text-base no-underline',
                          // Dividers start past the icon, as in a grouped list.
                          'not-first:before:bg-border not-first:before:absolute not-first:before:top-0 not-first:before:right-0 not-first:before:left-14 not-first:before:h-px',
                          'active:bg-muted',
                          active && 'font-semibold',
                        )}
                      >
                        <span
                          className={cn(
                            'flex size-[30px] shrink-0 items-center justify-center rounded-lg',
                            active
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-foreground',
                          )}
                        >
                          <Icon className="size-[18px]" strokeWidth={active ? 2.25 : 1.75} />
                        </span>
                        {place.label}
                      </Link>
                    );
                  })}
                </div>
              ))}
            </nav>
            </FocusTrap>
          </motion.div>
        </Overlay>
      )}
    </Layer>
  );
};
